import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const ABTests = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [currentTest, setCurrentTest] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', hypothesis: '', success_metric: ''
  });

  useEffect(() => {
    fetchTests();
    fetchAnalytics();
  }, []);

  const fetchTests = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/abtesting/tests');
      setTests(response.data.tests || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch tests');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await api.get('/api/abtesting/analytics/overview');
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  };

  const handleCreate = () => {
    setCurrentTest(null);
    setFormData({ name: '', description: '', hypothesis: '', success_metric: '' });
    setShowDialog(true);
  };

  const handleEdit = (test) => {
    setCurrentTest(test);
    setFormData({
      name: test.name,
      description: test.description || '',
      hypothesis: test.hypothesis || '',
      success_metric: test.success_metric || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this test?')) return;
    try {
      await api.delete(`/api/abtesting/tests/${id}`);
      setSuccess('Test deleted');
      fetchTests();
      fetchAnalytics();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentTest) {
        await api.put(`/api/abtesting/tests/${currentTest.id}`, formData);
        setSuccess('Test updated');
      } else {
        await api.post('/api/abtesting/tests', formData);
        setSuccess('Test created');
      }
      setShowDialog(false);
      fetchTests();
      fetchAnalytics();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'badge-secondary',
      running: 'badge-primary',
      completed: 'badge-success',
      cancelled: 'badge-danger'
    };
    return colors[status] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">A/B Tests</span>
      </nav>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '3px solid #EF4444' }}>
          <div>
            <h2 style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem 0' }}>
              🧪 A/B Testing
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Manage experiments and analyze variant performance
            </p>
          </div>
          <button onClick={handleCreate} className="btn btn-primary">➕ Add Test</button>
        </div>

        {error && (
          <div style={{ backgroundColor: 'var(--red-50)', border: '1px solid var(--red-200)', color: 'var(--red-800)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setError(null)}>
            {error}
          </div>
        )}

        {success && (
          <div style={{ backgroundColor: 'var(--success-50)', border: '1px solid var(--success-500)', color: 'var(--success-700)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setSuccess(null)}>
            {success}
          </div>
        )}

        {analytics && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Total Tests</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.25rem' }}>{analytics.total_tests}</div>
            </div>
            <div style={{ padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Running</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.25rem' }}>{analytics.running_tests}</div>
            </div>
            <div style={{ padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
              <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Completed</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.25rem' }}>{analytics.completed_tests}</div>
            </div>
          </div>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Name</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Success Metric</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Status</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Winner</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: 'var(--gray-700)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {tests.length === 0 ? (
              <tr><td colSpan="5" style={{ padding: '2rem', textAlign: 'center', color: 'var(--gray-500)' }}>No tests found</td></tr>
            ) : (
              tests.map((test) => (
                <tr key={test.id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                  <td style={{ padding: '0.75rem' }}>{test.name}</td>
                  <td style={{ padding: '0.75rem' }}>{test.success_metric}</td>
                  <td style={{ padding: '0.75rem' }}>
                    <span className={getStatusColor(test.status)}>
                      {test.status || 'draft'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    {test.winner_variant ? (
                      <span className="badge badge-success">{test.winner_variant}</span>
                    ) : '-'}
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <button onClick={() => handleEdit(test)} className="btn btn-sm btn-outline" style={{ marginRight: '0.5rem' }}>Edit</button>
                    <button onClick={() => handleDelete(test.id)} className="btn btn-sm btn-danger">Delete</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="card" style={{ width: '90%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ marginBottom: '1rem' }}>{currentTest ? 'Edit Test' : 'New Test'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Name *</label>
                <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="form-input" />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Description</label>
                <textarea rows="2" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="form-input" />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Hypothesis</label>
                <textarea rows="3" value={formData.hypothesis} onChange={(e) => setFormData({...formData, hypothesis: e.target.value})} className="form-input" placeholder="What do you expect to happen?" />
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500' }}>Success Metric *</label>
                <input type="text" required value={formData.success_metric} onChange={(e) => setFormData({...formData, success_metric: e.target.value})} className="form-input" placeholder="e.g., conversion_rate, click_rate" />
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowDialog(false)} className="btn btn-outline">Cancel</button>
                <button type="submit" className="btn btn-primary">{currentTest ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ABTests;
